// DOM-обёртки ассетов: иконка с запасным эмодзи, фон экрана, 9-slice панелей и кнопок через CSS border-image.
// Всё работает и без манифеста — тогда виден прежний вид (эмодзи, CSS-градиенты, плоские панели).
import { useEffect } from 'preact/hooks';
import { assetEntry, assetUrl, bgId, manifest, manifestLoaded, markBroken, pickFile, type Manifest } from './manifest.js';
import { viewport } from '../telegram/viewport.js';

/** Иконка интерфейса: ассет ICO-* или запасной символ того же размера (ux/13: иконки вместо эмодзи). */
export function Icon(p: { id: string; fallback: string; size?: number; label?: string; class?: string }) {
  void manifest.value;
  const url = assetUrl(p.id);
  const size = p.size ?? 20;
  // Пока манифест не пришёл — пустое место того же размера: запасной символ только если ассета действительно нет.
  if (!url && !manifestLoaded.value) return <span class={`ico ${p.class ?? ''}`} style={{ width: size, height: size, display: 'inline-block' }} aria-hidden />;
  const a11y = p.label ? { role: 'img' as const, 'aria-label': p.label } : { 'aria-hidden': true };
  if (!url) return <span class={`ico ${p.class ?? ''}`} style={{ fontSize: size * 0.9, lineHeight: `${size}px`, width: size, display: 'inline-block', textAlign: 'center' }} {...a11y}>{p.fallback}</span>;
  return <img class={`ico ${p.class ?? ''}`} src={url} width={size} height={size} alt={p.label ?? ''} draggable={false} onError={() => markBroken(p.id)} style={{ objectFit: 'contain', verticalAlign: 'middle' }} {...a11y} />;
}

/** Фон экрана за содержимым: картинка из манифеста, иначе CSS-фон темы. */
export function ScreenBg(p: { id: string; dim?: number }) {
  void manifest.value;
  const id = bgId(p.id, viewport.value.w);
  const url = assetUrl(id);
  const e = assetEntry(id);
  const pos = typeof e?.anchor === 'string' ? `center ${e.anchor}` : 'center';
  useEffect(() => {
    document.documentElement.dataset.bg = url ? '1' : '0';
    return () => { document.documentElement.dataset.bg = '0'; };
  }, [url]);
  if (!url) return null;
  return (
    <div class="screen-art-bg" aria-hidden data-bg={id} style={{ backgroundImage: `url("${url}")`, backgroundPosition: pos, ['--dim' as string]: String(p.dim ?? 0.45) }}>
      <img src={url} alt="" style={{ display: 'none' }} onError={() => markBroken(id)} />
    </div>
  );
}

/**
 * Какие классы красит какой 9-slice. Новые панели — строкой здесь, без правки компонентов.
 * scale — доля отступов рамки в CSS px: у арта рамки широкие (окно — 34 px), без уменьшения кнопка 52 pt
 * и лист на 360 px теряют место под содержимое.
 */
export const NINE_SLICE: Record<string, { sel: string; scale: number; ink?: Ink }> = {
  'PANEL-CARD': { sel: '.card, .list-row', scale: 0.6, ink: 'dark' },
  'PANEL-CHIP': { sel: '.chip', scale: 0.35, ink: 'light' },
  'PANEL-HEADER': { sel: '.screen-title', scale: 0.3, ink: 'light' },
  'PANEL-SHEET': { sel: '.sheet, .dialog', scale: 0.5, ink: 'dark' },
  'PANEL-TIN': { sel: '.banner', scale: 0.5, ink: 'dark' },
  'PANEL-PAPER': { sel: '.tut-bubble .txt', scale: 0.6, ink: 'dark' },
  'BTN-ACCENT': { sel: '.btn.accent:not(:disabled):not(.disabled)', scale: 0.5, ink: 'accent' },
  'BTN-OUTLINE': { sel: '.btn.outline:not(:disabled)', scale: 0.5, ink: 'onGreen' },
};

/**
 * Цвет текста задаёт АРТ панели, а не тема Telegram: рамки и заливки 9-slice одинаковы в светлой и тёмной теме.
 * dark — светлые панели (бумага, жесть, окно): токены светлой темы ux/12 §6.1, контраст ≥ 4.5:1.
 * light — тёмные плашки (UI-CHIP, UI-HEADER, заливка ≈ #423f3d): светлый текст, иначе в светлой теме тёмное по тёмному.
 * accent — оранжевая кнопка UI-BTN-PRIMARY (≈ #ef8b22): тёмный текст #1A1410 везде (рецепт арта).
 * onGreen — зелёная кнопка UI-BTN-SECONDARY (≈ #607e58): белый текст с тенью (4.55:1; тёмный даёт 3.7).
 */
type Ink = 'dark' | 'light' | 'accent' | 'onGreen';
const DARK_INK = 'color:#1f1b16;--text:#1f1b16;--text-2:#5e554c;--accent-text:#8a4409;--positive:#22622a;--negative:#9a3009;'
  + '--warning:#7a4f00;--surface:#fbf3e3;--surface-2:#eadcc2;--r-common:#5e554c;--r-good:#22622a;--r-rare:#1f63b0;--r-epic:#7b3fb8;--r-legendary:#8a5a00;';
const LIGHT_INK = 'color:#f1ece4;--text:#f1ece4;--text-2:#d2c9bc;--accent-text:#f5b068;--positive:#8fdc8f;--negative:#ff9a7a;--warning:#f0c060;';
const INKS: Record<Ink, string> = {
  dark: DARK_INK, light: LIGHT_INK,
  accent: 'color:#1a1410;--text:#1a1410;--on-accent:#1a1410;text-shadow:none;',
  onGreen: 'color:#ffffff;--text:#ffffff;text-shadow:0 1px 2px rgba(0,0,0,.55);',
};

/** CSS border-image для 9-slice: срез в px исходника (@1x × плотность), ширина рамки — в CSS px. */
export function nineSliceCss(m: Manifest, dpr: number, base: string): string {
  let css = '';
  for (const [id, { sel, scale, ink }] of Object.entries(NINE_SLICE)) {
    const e = m[id];
    if (!e?.slice) continue;
    const f = pickFile(e, dpr);
    const k = f.density;
    const [t, r, b, l] = e.slice;
    const w = (n: number) => `${Math.round(n * scale)}px`;
    css += `${sel}{border-style:solid;border-color:transparent;border-width:${w(t)} ${w(r)} ${w(b)} ${w(l)};`
      + `border-image:url("${base}${f.src}") ${t * k} ${r * k} ${b * k} ${l * k} fill / ${w(t)} ${w(r)} ${w(b)} ${w(l)} stretch;background:none;`
      + (ink ? INKS[ink] : '') + '}\n';
  }
  return css;
}

/**
 * Ставит правила 9-slice в <style id="nine-slice">, когда манифест пришёл или сменился. Правило попадает в CSS
 * только после того, как картинка загрузилась: у border-image нет onerror, и пропавший файл сделал бы панели
 * и кнопки прозрачными. Не загрузилась — ID помечается сломанным, остаётся прежний CSS.
 */
export function NineSliceStyles() {
  const m = manifest.value;
  useEffect(() => {
    let alive = true;
    const dpr = devicePixelRatio || 1;
    const base = import.meta.env.BASE_URL;
    const ids = Object.keys(NINE_SLICE).filter((id) => m[id]?.slice);
    void Promise.all(ids.map((id) => new Promise<string | null>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(id);
      img.onerror = () => { markBroken(id); resolve(null); };
      img.src = `${base}${pickFile(m[id]!, dpr).src}`;
    }))).then((ok) => {
      if (!alive) return;
      const loaded: Manifest = {};
      for (const id of ok) if (id) loaded[id] = m[id]!;
      let el = document.getElementById('nine-slice') as HTMLStyleElement | null;
      if (!el) { el = document.createElement('style'); el.id = 'nine-slice'; document.head.appendChild(el); }
      el.textContent = nineSliceCss(loaded, dpr, base);
    });
    return () => { alive = false; };
  }, [m]);
  return null;
}

export { assetEntry };
